import { CustomPageType } from '@ViewModels';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import { FC, useCallback, useEffect, useState } from 'react';
import { MastheadPortalId } from '../../../../models/AdminModels';
import { invalidateHostedBlogPagePreview } from '../../../../queries';
import {
	invalidateFindOrCreateBlogStylesByTypeQueries,
	useFindOrCreateBlogStylesByTypeQuery,
} from '../../../../queries/CustomPageStyle/useFindOrCreateBlogStylesByTypeQuery';
import { ConfirmationDialog, DefaultConfirmationOptions } from '../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { MultiContainerHeader } from '../../../../web/components/MultiContainerHeader';
import { WarningIcon } from '../../../../web/components/svgs/icons/WarningIcon';
import { bs } from '../../../../web/styles/styles';
import { useAccountContext } from '../../../components/blogs/AccountContext';
import { BreadcrumbsBarWithNameProvider } from '../../../components/blogs/BreadcrumbsBarWithNameProvider';
import { HtmlPreviewFrame } from '../../../components/blogs/HtmlPreviewFrame';
import { FooterDesignForm } from './Forms/FooterDesignForm';
import { HeaderDesignForm } from './Forms/HeaderDesignForm';
import { HomeDesignForm } from './Forms/HomeDesignForm';
import { PostDesignForm } from './Forms/PostDesignForm';
import { styleSheet } from './styles';

export const TemplateDesignFormContainer: FC = () => {
	const { account, impersonationContext } = useAccountContext();

	const blogFeature = account?.features?.blogFeature;
	const isBlogFeatureEnabled = blogFeature?.enabled;
	const isDudaHosted = !!blogFeature?.dudaSiteId;
	const isLevitateBlogEnabled = !isDudaHosted && isBlogFeatureEnabled;

	const [isDirty, setIsDirty] = useState<boolean>(false); // data in form has been edited, not saved
	const [selectedPageType, setSelectedPageType] = useState<CustomPageType>(CustomPageType.Header);
	const [pendingTab, setPendingTab] = useState<CustomPageType | null>(null);

	const { data, isSuccess } = useFindOrCreateBlogStylesByTypeQuery({
		impersonationContext,
		type: selectedPageType,
		meta: {
			toastErrorMessage: 'Error using custom blog styles',
		},
	});
	useEffect(() => {
		if (isSuccess) {
			setIsDirty(false);
		}
	}, [isSuccess]);

	const handleFormUpdateSuccess = () => {
		invalidateFindOrCreateBlogStylesByTypeQueries(selectedPageType);
		invalidateHostedBlogPagePreview(selectedPageType);
	};

	const handleValueChange = (value: CustomPageType) => {
		if (!isDirty) {
			setSelectedPageType(value);
		} else {
			setPendingTab(value);
		}
	};

	const handleDialogClose = (_: never, canceled: boolean) => {
		if (!canceled) {
			setSelectedPageType(pendingTab);
			setIsDirty(false);
		}
		setPendingTab(null);
	};

	const dynamicTabStyles = (pageType: CustomPageType) => {
		return selectedPageType === pageType ? styleSheet.selectedTab : styleSheet.backgroundTab;
	};

	const handleFormDirty = useCallback(() => {
		setIsDirty(true);
	}, []);

	return (
		<div className='admin-blog-design-form-container'>
			{account ? (
				<>
					<MultiContainerHeader
						appBarHeader={<BreadcrumbsBarWithNameProvider account={account} currentLocation='Template Designer' />}
						appBarPortalDestinationId={MastheadPortalId}
					/>
					<Tabs.Root
						className={css(styleSheet.tabRoot)}
						onValueChange={handleValueChange as (value: string) => void}
						value={selectedPageType}
					>
						<Tabs.List className={css(styleSheet.tabList)}>
							<Tabs.Trigger
								className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.Header))}
								value={CustomPageType.Header}
							>
								Header
							</Tabs.Trigger>
							<div className={css(styleSheet.tabListBorder, styleSheet.tabListSpacer)} />
							{isLevitateBlogEnabled && (
								<>
									<Tabs.Trigger
										className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.BlogHome))}
										value={CustomPageType.BlogHome}
									>
										Blog Home
									</Tabs.Trigger>
									<div className={css(styleSheet.tabListBorder, styleSheet.tabListSpacer)} />
									<Tabs.Trigger
										className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.BlogPost))}
										value={CustomPageType.BlogPost}
									>
										Blog Post
									</Tabs.Trigger>
									<div className={css(styleSheet.tabListBorder, styleSheet.tabListSpacer)} />
								</>
							)}
							<Tabs.Trigger
								className={css(styleSheet.tab, dynamicTabStyles(CustomPageType.Footer))}
								value={CustomPageType.Footer}
							>
								Footer
							</Tabs.Trigger>
							<div className={css(styleSheet.tabListBorder, bs.wFull)} />
						</Tabs.List>
						<Tabs.Content value={CustomPageType.Header}>
							<HeaderDesignForm
								customContent={data?.type === CustomPageType.Header && data}
								onFormDirty={handleFormDirty}
								onUpdateSuccess={handleFormUpdateSuccess}
							/>
						</Tabs.Content>
						{isLevitateBlogEnabled && (
							<>
								<Tabs.Content value={CustomPageType.BlogHome}>
									<HomeDesignForm
										customContent={data?.type === CustomPageType.BlogHome && data}
										onFormDirty={handleFormDirty}
										onUpdateSuccess={handleFormUpdateSuccess}
									/>
								</Tabs.Content>
								<Tabs.Content value={CustomPageType.BlogPost}>
									<PostDesignForm
										customContent={data?.type === CustomPageType.BlogPost && data}
										onFormDirty={handleFormDirty}
										onUpdateSuccess={handleFormUpdateSuccess}
									/>
								</Tabs.Content>
							</>
						)}
						<Tabs.Content value={CustomPageType.Footer}>
							<FooterDesignForm
								customContent={data?.type === CustomPageType.Footer && data}
								onFormDirty={handleFormDirty}
								onUpdateSuccess={handleFormUpdateSuccess}
							/>
						</Tabs.Content>
					</Tabs.Root>
					<div className={css(bs.pb7)} />
					<HtmlPreviewFrame type={selectedPageType} />
				</>
			) : (
				<LoadingSpinner />
			)}
			{pendingTab && (
				<ConfirmationDialog
					icon={<WarningIcon />}
					modalProps={{ isOpen: !!pendingTab, onRequestClose: handleDialogClose }}
					options={DefaultConfirmationOptions}
					title='Changing tabs without saving will clear pending edits'
				/>
			)}
		</div>
	);
};
